import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  Signal,
  signal,
  SimpleChanges,
} from '@angular/core';
import { difference } from 'lodash';
import { DsChipColorVariant } from '@levelaccess/design-system';
import { toSignal } from '@angular/core/rxjs-interop';

import { ActivatedRoute } from '@angular/router';
import { combineLatest, Observable, of, ReplaySubject, Subscription } from 'rxjs';
import { map, take } from 'rxjs/operators';

import { Api, ApiQueryOption } from '../../../../../../../shared/constants/api';
import { AccessibilityAuditToolNames } from '../../../../../../../shared/constants/audit-tool';
import { $digitalProperty } from '../../../../../../../shared/constants/digital-properties';
import { FileExtension } from '../../../../../../../shared/constants/file-extension';
import { $scan } from '../../../../../../../shared/constants/scan';
import { $scanSummaries } from '../../../../../../../shared/constants/scan-summaries';
import { $scanTag, SystemTags } from '../../../../../../../shared/constants/scan-tag';
import { $scanViewport, ScanViewportType } from '../../../../../../../shared/constants/scan-viewport';
import { ScanOrigin, standardNameAndLevel } from '../../../../../../../shared/constants/scanning';
import { MobileScanOriginUtility } from '../../../../../utility/mobile-scan-origin.utility';
import { IDigitalPropertyDetails } from '../../../../../../../shared/interfaces/digital-property.interface';
import { IScanSummaryAllItem } from '../../../../../../../shared/interfaces/scan-summaries.interface';
import { IScanViewport } from '../../../../../../../shared/interfaces/scan-viewport.interface';
import { IScan } from '../../../../../../../shared/interfaces/scan.interface';
import { SharedCommonUtility } from '../../../../../../../shared/utils/common.utility';
import { SharedDateUtility } from '../../../../../../../shared/utils/date.utility';
import { IModalDialogInputData } from '../../../../../interfaces/modal-dialog.interface';
import { AclSecurityAdapter } from '../../../../../services/acl.service';
import { AsyncReportService } from '../../../../../services/async-report.service';
import { DigitalPropertyService } from '../../../../../services/digital-property.service';
import { UserAclService } from '../../../../../services/user-acl.service';
import { TranslateService } from '../../../../../translate/translate.service';
import { UserDigitalPropertyService } from '../../../../../services/user-digital-property.service';
import { ShareReportModalInputData } from './share-report-modal/share-report-modal.component';
import { AngularUtility } from '../../../../../utility/angular.utility';
import { IRequiredSecurity } from '../../../../../../../shared/interfaces/security.interface';
import { RequiredSecurities } from '../../../../../../../shared/constants/required-securities';
import { ScanReportHandler, ScanReportHandlerProvider } from './scan-report-handler';
import { NavService } from '../../../../../services/navigation/nav.service';
import { FeatureFlagService } from '../../../../../services/feature-flag/feature-flag.service';
import { FeatureFlagCollection } from '../../../../../../../shared/interfaces/feature-flag.interface';

@Component({
  selector: 'app-view-scan-results',
  templateUrl: './view-scan-results-summary.component.html',
  styleUrls: ['./view-scan-results-summary.component.scss'],
  providers: [AsyncReportService, ScanReportHandlerProvider],
  standalone: false,
})
export class ViewScanResultsSummaryComponent implements OnInit, OnDestroy, OnChanges {
  @Input() public auditTool: AccessibilityAuditToolNames;
  @Input() public standard: standardNameAndLevel;
  @Input() public scanSummary: IScanSummaryAllItem;
  @Input() public prevToolVersion: string;
  @Input() public currToolVersion: string;
  @Input() public toolsUsed: string[] = [];

  private _scan: IScan;
  @Input() set scan(scan: IScan | null) {
    if (SharedCommonUtility.isNullish(scan)) {
      return;
    }

    this._scan = scan;
    this.scanReportHandler = this.scanReportHandlerProvider.get(this._scan);
    this.checkDownloadReportPermission();
  }

  @Output() public onAuditToolChange: EventEmitter<AccessibilityAuditToolNames> = new EventEmitter();

  private subscriptions: Subscription = new Subscription();
  private _canDownloadReport$: ReplaySubject<boolean> = new ReplaySubject(1);
  public canDownloadReport$: Observable<boolean> = this._canDownloadReport$.asObservable();
  private scanReportHandler: ScanReportHandler;

  public auditToolNames: typeof AccessibilityAuditToolNames = AccessibilityAuditToolNames;
  public Api: typeof Api = Api;
  public $scanSummaries: typeof $scanSummaries = $scanSummaries;
  protected readonly DsChipColorVariant: typeof DsChipColorVariant = DsChipColorVariant;
  public fileExtensions$: Observable<FileExtension[]>;
  public modalInputData: IModalDialogInputData<ShareReportModalInputData>;
  public dpName$: Observable<string>;
  public $isMobileDigitalProperty: Signal<boolean> = signal(false);
  public readonly isVerticalNavigationEnabled: Signal<boolean>;
  public readonly findingsDetailsRefreshEnabled: Signal<boolean>;

  constructor(
    private activatedRoute: ActivatedRoute,
    private translateService: TranslateService,
    private asyncReportService: AsyncReportService,
    private userAclService: UserAclService,
    private featureFlagService: FeatureFlagService,
    private digitalPropertyService: DigitalPropertyService,
    private userDigitalPropertyService: UserDigitalPropertyService,
    private scanReportHandlerProvider: ScanReportHandlerProvider,
    private navService: NavService,
  ) {
    this.fileExtensions$ = this._canDownloadReport$.pipe(
      map((canDownloadPermission: boolean): FileExtension[] => {
        if (!canDownloadPermission) {
          return [];
        }
        return this.isMonitoringTag ? [FileExtension.xlsx] : [FileExtension.xlsx, FileExtension.csv];
      }),
    );

    this.$isMobileDigitalProperty = toSignal(this.userDigitalPropertyService.isMobileDigitalProperty$);
    this.isVerticalNavigationEnabled = toSignal(this.navService.isVerticalNavigationEnabled$, { initialValue: false });
    this.findingsDetailsRefreshEnabled = toSignal(
      this.featureFlagService.variation$(FeatureFlagCollection.findingsDetailsRefresh, false).pipe(map(Boolean)),
      { initialValue: false },
    );
  }

  public get scanTitle(): string {
    return this._scan?.[$scan.title] || '';
  }

  public get scanTag(): string {
    return this._scan?.[$scan.tag]?.[$scanTag.tagName] || '';
  }

  public get isMonitoringTag(): boolean {
    return this.scanTag === SystemTags.Monitoring;
  }

  public get scanViewPort(): IScanViewport {
    return (
      this._scan?.[$scan.viewport] || {
        [$scanViewport.viewportType]: ScanViewportType.desktop,
        [$scanViewport.height]: 600,
        [$scanViewport.width]: 800,
      }
    );
  }

  public get isMobileXmlScan(): boolean {
    return this._scan[$scan.scanOrigin] === ScanOrigin.mobile_xml;
  }

  public get scanOriginLabel(): string | null {
    const key: string | null = MobileScanOriginUtility.getTranslationKey(this._scan);
    return key ? this.translateService.instant(key) : null;
  }

  public get scanOriginChipVariant(): DsChipColorVariant {
    return MobileScanOriginUtility.getChipVariant(this._scan);
  }

  public get scanDate(): Date {
    return new Date(this._scan?.[$scan.processingStartedAt] || this._scan?.[$scan.createdAt]);
  }

  public get standardAndConformanceLevel(): string {
    return this.standard || '';
  }

  public get formattedScanDate(): string {
    return SharedDateUtility.getDateAtTime(this.scanDate);
  }

  public get totalScannedPages(): number {
    return this._scan?.[$scan.totalScannedPages] || 0;
  }

  public get totalCrawledPages(): number {
    return this._scan?.[$scan.totalCrawledPages] || 0;
  }

  public get formattedScanTool(): string {
    if (SharedCommonUtility.isNullishOrEmpty(this.currToolVersion)) {
      return null;
    }

    const labels: string[] = [this.translateService.instant(`scan_results_tool_option_${this.auditTool}`)];

    if (SharedCommonUtility.isNullishOrEmpty(this.prevToolVersion) || this.prevToolVersion === this.currToolVersion) {
      labels.push(`v${this.currToolVersion}`);
    }

    if (SharedCommonUtility.notNullishOrEmpty(this.prevToolVersion) && this.prevToolVersion !== this.currToolVersion) {
      labels.push(
        this.translateService.instant('tool_has_been_updated_to_version', [
          `v${this.prevToolVersion}`,
          `v${this.currToolVersion}`,
        ]),
      );
    }

    return labels.join(' ');
  }

  private checkDownloadReportPermission(): void {
    const requiredSecurity: IRequiredSecurity = this.scanReportHandler.getRequiredSecurityForDownload();

    this.subscriptions.add(
      this.userAclService
        .createCheckAccessForCurrentUser()
        .pipe(
          take(1),
          map((adapter: AclSecurityAdapter): boolean =>
            adapter
              .useRequiredSecurity(requiredSecurity, false)
              .useFunctionalActions(requiredSecurity.functionalActions)
              .useOptions({ requireFunctionalActions: true })
              .check(),
          ),
        )
        .subscribe((canDownload: boolean) => this._canDownloadReport$.next(canDownload)),
    );
  }

  public onDownloadReport(fileExtension?: FileExtension): void {
    if (SharedCommonUtility.isNullish(fileExtension)) {
      return;
    }

    const scanUrl: string = this.activatedRoute.snapshot.queryParams[ApiQueryOption.url];

    this.asyncReportService.init({
      getReport$: this.scanReportHandler.getFullScanReport(this.auditTool, fileExtension, scanUrl),
      postReport$: this.scanReportHandler.generateFullScanReport(this.auditTool, fileExtension, scanUrl),
      messages: {
        reportIsBeingGenerated: this.translateService.instant('report_is_being_generated'),
        reportHasBeenGenerated: (): Observable<string> =>
          this.dpName$.pipe(
            map((dpName: string) => {
              return this.getReportGeneratedMessage(dpName);
            }),
          ),
        reportIsDownloading: (): Observable<string> => {
          return of(this.translateService.instant('file_is_downloading'));
        },
        reportGenerationError: this.dpName$.pipe(
          map((dpName: string) => {
            return this.translateService.instantHtml('scan_report_generation_error', [dpName]);
          }),
        ),
        reportDownloadError: this.dpName$.pipe(
          map((dpName: string) => {
            return this.translateService.instantHtml('scan_report_download_error', [dpName]);
          }),
        ),
      },
    });

    this.asyncReportService.downloadReport();
  }

  public getReportGeneratedMessage(dpName: string): string {
    if (this.isMonitoringTag) {
      return this.translateService.instant('monitoring_scan_report_has_been_generated', dpName);
    }
    return this.translateService.instant('scan_report_has_been_generated', dpName);
  }

  public onToolSelect(tool: AccessibilityAuditToolNames): void {
    this.onAuditToolChange.emit(tool);
  }

  public get toolsToExclude(): AccessibilityAuditToolNames[] {
    return difference(Object.values(this.auditToolNames), this.toolsUsed) as AccessibilityAuditToolNames[];
  }

  private buildObservables(): void {
    this.dpName$ = this.digitalPropertyService
      .getDigitalProperty(String(this._scan[$scan.workspace]), String(this._scan[$scan.digitalProperty]))
      .pipe(
        map((dp: IDigitalPropertyDetails) => {
          return dp[$digitalProperty.name];
        }),
        AngularUtility.shareRef(),
      );
  }

  public ngOnInit(): void {
    this.buildObservables();
    this.subscriptions.add(
      this._canDownloadReport$.pipe(take(1)).subscribe((canDownloadReport: boolean) => {
        if (SharedCommonUtility.notNullish(this._scan) && canDownloadReport) {
          this.asyncReportService.triggerAutoDownload(this.onDownloadReport.bind(this));
        }
      }),
    );
  }

  public ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (SharedCommonUtility.notNullish(this._scan) && changes.auditTool?.previousValue !== changes.auditTool?.currentValue) {
      this.setShareModalData();
    }
  }

  private setShareModalData(): void {
    this.subscriptions.add(
      combineLatest([this.canShareReport$(), this.canLoadWorkspaceUsers$()])
        .pipe(take(1))
        .subscribe(([canShareReport, canLoadWorkspaceUsers]: [boolean, boolean]) => {
          if (canShareReport && canLoadWorkspaceUsers) {
            const workspaceId: string = String(this._scan[$scan.workspace]);
            const digitalPropertyId: string = String(this._scan[$scan.digitalProperty]);

            this.modalInputData = {
              data: {
                workspaceId: workspaceId,
                digitalPropertyId: digitalPropertyId,
                scan: { ...this._scan },
                auditTool: this.auditTool,
              },
            };
          } else {
            this.modalInputData = null;
          }
        }),
    );
  }

  private canShareReport$(): Observable<boolean> {
    const requiredSecurity: IRequiredSecurity = this.scanReportHandler.getRequiredSecurityForReportShare();

    return this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useRequiredSecurity(requiredSecurity, false)
            .useFunctionalActions(requiredSecurity.functionalActions)
            .useOptions({ requireFunctionalActions: true })
            .check(),
        ),
      );
  }

  private canLoadWorkspaceUsers$(): Observable<boolean> {
    return this.userAclService
      .createCheckAccessForCurrentUser()
      .pipe(
        map((adapter: AclSecurityAdapter): boolean =>
          adapter
            .useRequiredSecurity(RequiredSecurities.Workspaces_Read_No_Entity_Level, false)
            .useFunctionalActions(RequiredSecurities.Workspaces_Read_No_Entity_Level.functionalActions)
            .useOptions({ requireFunctionalActions: true })
            .check(),
        ),
      );
  }
}
